/**
 * Constants for Product Onboarding Component
 */

// Onboarding Types
export const ONBOARDING_TYPES = {
  PRODUCT: 'product',
  SKU: 'sku',
  BOTH: 'both',
} as const;

// SKU Types
export const SKU_TYPES = {
  SINGLE: 'single',
  SUITE: 'suite',
  ADDON: 'addon',
} as const;

// Provisioning Styles
export const PROVISIONING_STYLES = {
  SYNC: 'STYLE_SYNC',
  ASYNC: 'STYLE_ASYNC',
} as const;

// Buying Models
export const BUYING_MODELS = {
  EA: 'EA',
  ALC: 'ALC',
} as const;

// Table Keys for Bulk Insert API
export const TABLE_KEYS = {
  PRODUCTS: 'products',
  SKUS: 'skus',
  ENTITLEMENTS: 'entitlements',
  ORG_ENTITLEMENTS: 'org_entitlements',
  PRODUCT_SKU_CONFIGS: 'product_sku_configs',
  PRODUCT_SKU_CONFIG_ENTITLEMENTS: 'product_sku_config_entitlements',
  SKU_ORG_ENTITLEMENT_CONFIGS: 'sku_org_entitlement_configs',
  PRODUCT_ALLOWED_REGIONS: 'product_allowed_regions',
  PROVISIONING_QUESTIONS: 'provisioning_questions',
  PRODUCT_ADDON_CONFIG: 'product_addon_config',
} as const;

// Events
export const PRODUCT_EVENTS = {
  PRODUCT_EVENT: 'ProductEvent',
  REMOVE_PRODUCT_EVENT: 'RemoveProductEvent',
} as const;

// Color Mappings for Tags
export const TAG_COLORS = {
  ONBOARDING_TYPE: {
    PRODUCT: 'blue',
    SKU: 'green',
    BOTH: 'purple',
  },
  SKU_RELATIONSHIP: {
    SINGLE: 'cyan',
    SUITE: 'cyan',
    ADDON: 'cyan',
  },
  ENTITLEMENT_TYPE: {
    STANDARD: 'geekblue',
    ORG: 'magenta',
  },
  STATUS: {
    NEW: 'blue',
    EXISTING: 'green',
    EXTERNAL: 'orange',
  },
} as const;

// Form Labels
export const FORM_LABELS = {
  ONBOARDING_TYPE: 'Onboarding Type',
  PRODUCT_TYPE: 'Product Type',
  SKU_TYPE: 'SKU Type',
  PRODUCT_NAME: 'Product Name',
  PRODUCT_KEY: 'Product Key',
  PROVISIONING_CLIENT: 'Provisioning Client',
  PROVISIONING_STYLE: 'Provisioning Style',
  SKU_NAME: 'SKU Name',
  EXTERNAL_SKU: 'External SKU',
  BUYING_MODEL: 'Buying Model',
  SKU_RELATIONSHIP: 'SKU-Product Relationship',
  ENTITLEMENT_TYPE: 'Entitlement Type',
  ALLOWED_REGIONS: 'Allowed Regions',
} as const;

// Messages
export const MESSAGES = {
  SUCCESS: {
    ONBOARDING_COMPLETE: 'Onboarding completed successfully!',
    DATA_LOADED: 'Data loaded successfully',
  },
  ERROR: {
    LOAD_DATA_FAILED: 'Failed to load initial data',
    VALIDATION_FAILED: 'Please fill in all required fields',
    ONBOARDING_FAILED: 'Failed to complete onboarding',
  },
  INFO: {
    CHOOSE_WORKFLOW: 'Choose your onboarding workflow',
    SELECT_WORKFLOW_DESC: 'Select whether you want to onboard a product, SKU, or both together. This will determine the subsequent steps.',
    REVIEW_BEFORE_SUBMIT: 'Please review all the details before submitting.',
    SKU_ONLY_ONBOARDING: 'You\'re onboarding SKUs. You can add multiple SKUs and link them to existing products.',
    DEPENDENT_SERVICE_CONFIG: 'Select a dependent service product (org-entitled) and its entitlements to create SKU org entitlement configurations.',
    ORG_ENTITLED_INFO: 'Org entitled products will insert into both entitlements and org_entitlements tables',
    STANDARD_ENTITLED_INFO: 'Standard entitlements will only insert into the entitlements table',
  },
} as const;

// Step Titles
export const STEP_TITLES = {
  ONBOARDING_TYPE: 'What would you like to onboard?',
  PRODUCT_INFO: 'Product Information',
  SKU_CONFIG: 'SKU Configuration',
  ENTITLEMENTS: 'Entitlement Configuration',
  ADDITIONAL_SETTINGS: 'Additional Settings',
  REVIEW: 'Review Your Configuration',
} as const;

// Button Labels
export const BUTTON_LABELS = {
  CONTINUE: 'Continue',
  PREVIOUS: 'Previous',
  NEXT_SKU: 'Next: SKU Configuration',
  NEXT_ENTITLEMENTS: 'Next: Entitlements',
  NEXT_ADDITIONAL: 'Next: Additional Settings',
  NEXT_REVIEW: 'Next: Review',
  BACK_TO_EDIT: 'Back to Edit',
  CONFIRM_SUBMIT: 'Confirm & Submit',
  ADD_SKU: 'Add SKU',
  ADD_PRODUCT: 'Add Product in Suite',
  ADD_ENTITLEMENT: 'Add Entitlement',
  ADD_ORG_ENTITLEMENT: 'Add Org Entitlement',
  ADD_QUESTION: 'Add Provisioning Question',
  ADD_ORG_ENTITLEMENT_CONFIG: 'Add Org Entitlement Config',
} as const;

// Question Types for Provisioning
export const QUESTION_TYPES = {
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  SELECT: 'SELECT',
  MULTI_SELECT: 'MULTI_SELECT',
  BOOLEAN: 'BOOLEAN',
} as const;

// Access Types for Org Entitlements
export const ACCESS_TYPES = {
  READ: 'READ',
  WRITE: 'WRITE',
  ADMIN: 'ADMIN',
  FULL: 'FULL',
} as const;

// Table Column Labels
export const TABLE_COLUMNS = {
  SKU_NAME: 'SKU Name *',
  EXTERNAL: 'External',
  BUYING_MODEL: 'Buying Model *',
  RELATIONSHIP: 'Relationship *',
  ACTION: 'Action',
  PRODUCT: 'Product *',
  OPERATION: 'Operation',
  OPERAND: 'Operand',
  FORCE_REGION: 'Force Region',
  ENTITLEMENTS: 'Entitlements *',
  ORG_ENTITLEMENTS: 'Org Entitlements *',
} as const;

// Styling Constants
export const STYLES = {
  CARD_PADDING: '24px',
  SMALL_CARD_PADDING: '12px',
  BORDER_RADIUS: '8px',
  BOX_SHADOW: '0 2px 8px rgba(0, 0, 0, 0.1)',
  PRIMARY_COLOR: '#1890ff',
  SUCCESS_COLOR: '#52c41a',
  ERROR_COLOR: '#ff4d4f',
  WARNING_COLOR: '#faad14',
} as const;

// Validation Rules
export const VALIDATION_RULES = {
  REQUIRED: { required: true, message: 'This field is required' },
  REQUIRED_FIELD: (fieldName: string) => ({
    required: true,
    message: `Please enter ${fieldName}`
  }),
  REQUIRED_SELECT: (fieldName: string) => ({
    required: true,
    message: `Please select ${fieldName}`
  }),
  MIN_LENGTH: (min: number) => ({
    min,
    message: `Must be at least ${min} characters`,
  }),
} as const;

// Default Values
export const DEFAULT_VALUES = {
  INITIAL_SKU_TYPE: 'single' as const,
  DEFAULT_ONBOARDING_TYPE: 'both' as const,
  INITIAL_STEP: 0,
} as const;

// Descriptions for each step card
export const STEP_DESCRIPTIONS = {
  PRODUCT_ONLY: 'Onboard a new product with entitlements, regions, and provisioning questions.',
  SKU_ONLY: 'Onboard a standalone SKU (1:1) or suite SKU (1:Many) and link to existing products.',
  PRODUCT_AND_SKU: 'Complete onboarding workflow for both product and SKU in one go.',
} as const;

// Tooltips for Product Flag boolean fields
export const PRODUCT_FLAG_TOOLTIPS = {
  IS_HIDDEN: 'When enabled, this product is hidden from external UI listings and customer-facing APIs. Combined with Auto Provision, it marks the product as a background platform product that is provisioned silently.',
  IS_AUTO_PROVISION: 'When enabled, this product is provisioned automatically without user interaction, and no provisioning emails are sent to the customer. Combined with Is Hidden, this marks the product as a background platform product.',
  IS_COMPOSITE: 'Marks this product as a NextGen SASE composite product (e.g., Cisco SASE). When enabled, product instances are placed directly into ACTION_REQUIRED state instead of following the standard provisioning flow. Standard provisioning and deprovisioning via RecalculateServices is skipped for composite products.',
  IS_BROWNFIELD_ONLY: 'Indicates this product is only for existing deployments (brownfield migrations). When enabled, provisioning status is set to IN_PROGRESS, an activation code is generated for the customer to link their existing tenant, and the product is skipped in standard service recalculation provisioning flows.',
  IS_UNIFIED_ONBOARDING_SUPPORTED: 'Enables the unified onboarding flow, allowing customers to select an existing tenant during provisioning. When enabled and the customer chooses \'use existing tenant\' (or the product is brownfield-only), an activation code is generated. This flag is also exposed via the service list API so the frontend knows to show the existing-tenant option.',
  IS_PARENT_PROVISIONING_SUPPORTED: 'Enables MSP parent-based provisioning. When an MSP manager org subscribes to this product and provisioning completes, the managed org\'s product instance is automatically updated with the manager\'s tenant ID. Required for multi-org MSP scenarios where a manager\'s provisioning drives downstream managed org provisioning.',
  INHERIT_PARENT_METADATA: 'When NextGen SASE Phase 2 provisioning is enabled, this product will inherit provisioning metadata (user intent and provisioning question answers) from its parent composite product. Used for child products in a SASE composite hierarchy (e.g., Cisco Secure Access inheriting answers from the parent SASE composite product).',
} as const;

// Tags for step descriptions
export const STEP_TAGS = {
  PRODUCT_ONLY: ['Product Details', 'Entitlements', 'Regions', 'Questions'],
  SKU_ONLY: ['SKU Details', 'Product Linking', 'Standalone or Suite'],
  PRODUCT_AND_SKU: ['Product', 'SKU', 'Entitlements', 'Full Setup'],
} as const;
